import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { resolveMutationErrorMessage } from "@/shared/api/error-message";
import { useArchivePrice } from "./use-archive-price";
import { formatMoney } from "./format-money";
import type { StripePrice } from "./types";

interface ArchivePriceDialogProps {
  readonly price: StripePrice | null;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onSuccess: () => void;
}

export function ArchivePriceDialog({
  price,
  open,
  onOpenChange,
  onSuccess,
}: ArchivePriceDialogProps) {
  const { t } = useTranslation();
  const archive = useArchivePrice();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) setError(null);
  }, [open]);

  if (!price) return null;

  const handleClose = (next: boolean) => {
    if (!next && archive.isPending) return;
    onOpenChange(next);
  };

  const handleConfirm = () => {
    setError(null);
    archive.mutate(price.id, {
      onSuccess: () => onSuccess(),
      onError: (err) => {
        setError(
          resolveMutationErrorMessage(err, t, {
            forbiddenKey: "catalog.errors.forbidden",
            serverPrefixKey: "catalog.errors.serverPrefix",
            genericKey: "catalog.prices.archive.error",
          }),
        );
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="archive-price-dialog">
        <DialogHeader>
          <DialogTitle>
            {t("catalog.prices.archive.title", {
              amount: formatMoney(price.unitAmount, price.currency),
            })}
          </DialogTitle>
          <DialogDescription>{t("catalog.prices.archive.description")}</DialogDescription>
        </DialogHeader>

        {error && (
          <p
            data-testid="archive-price-error"
            role="alert"
            className="font-sans text-sm font-medium text-destructive"
          >
            {error}
          </p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} disabled={archive.isPending}>
            {t("catalog.actions.cancel")}
          </Button>
          <Button
            data-testid="confirm-archive-price-button"
            variant="destructive"
            onClick={handleConfirm}
            disabled={archive.isPending}
          >
            {archive.isPending
              ? t("catalog.prices.archive.pending")
              : t("catalog.prices.actions.archive")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
